import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Search, CheckCircle, MinusCircle, AlertTriangle, Pencil } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import apiClient from '@/services/apiClient';

const ProcessingInwardHistory = () => {
  const navigate = useNavigate();
  const [receipts, setReceipts] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);

  // Admin: per-row edit qty
  const currentUser = (() => {
    try { return JSON.parse(localStorage.getItem('user') || '{}'); } catch { return {}; }
  })();
  const isAdmin = currentUser?.role === 'Admin';
  const [editRow, setEditRow] = useState(null); // the receipt being edited
  const [editNewQty, setEditNewQty] = useState('');
  const [editReason, setEditReason] = useState('');
  const [editLoading, setEditLoading] = useState(false);

  const fetchHistory = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: String(page), limit: '20' });
      if (search.trim()) params.append('bundle_code', search.trim());
      const res = await apiClient.get(`/processing-inward/history?${params}`);
      setReceipts(res.data.receipts || []);
      setTotal(res.data.total || 0);
    } catch {
      setReceipts([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchHistory(); }, [page]);

  const openEdit = (r) => {
    setEditRow(r);
    setEditNewQty(String(r.received_qty ?? ''));
    setEditReason('');
  };

  const closeEdit = () => {
    setEditRow(null);
    setEditNewQty('');
    setEditReason('');
  };

  const submitEdit = async () => {
    if (!editRow) return;
    const nq = parseInt(editNewQty, 10);
    if (isNaN(nq) || nq < 0) { toast.error('Enter a valid new qty (>= 0)'); return; }
    if (nq === Number(editRow.received_qty)) {
      toast.info('No change — new qty equals current'); return;
    }
    if (!window.confirm(
      `Update ${editRow.bundle_code} received qty: ${editRow.received_qty} → ${nq}?\n\n` +
      `This will claim or release PI USNs accordingly. Release is blocked if USNs have moved downstream.`
    )) return;
    setEditLoading(true);
    try {
      const resp = await apiClient.post('/processing-inward/admin/edit-bundle-qty', {
        bundle_code: editRow.bundle_code,
        new_qty: nq,
        reason: editReason.trim(),
      });
      const d = resp.data;
      toast.success(
        `Qty updated ${d.old_qty} → ${d.new_qty}` +
        (d.added_usns?.length ? ` (+${d.added_usns.length} new)` : '') +
        (d.removed_usns?.length ? ` (-${d.removed_usns.length} released)` : '')
      );
      closeEdit();
      fetchHistory();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update qty');
    } finally {
      setEditLoading(false);
    }
  };

  const statusIcon = (s) => {
    if (s === 'confirmed') return <CheckCircle className="w-4 h-4 text-green-600" />;
    if (s === 'shortage') return <MinusCircle className="w-4 h-4 text-red-600" />;
    return <AlertTriangle className="w-4 h-4 text-amber-600" />;
  };

  return (
    <div className="min-h-screen bg-gray-50" data-testid="processing-inward-history">
      <div className="bg-white border-b px-4 py-3 flex items-center gap-3 sticky top-0 z-10">
        <Button variant="ghost" size="sm" onClick={() => navigate('/sections/inhouse/processing-inward')} data-testid="back-btn">
          <ArrowLeft className="w-4 h-4" />
        </Button>
        <h1 className="text-lg font-bold text-gray-900">Processing Inward — History</h1>
        {isAdmin && (
          <Badge className="ml-2 bg-amber-100 text-amber-800 border border-amber-300" data-testid="admin-mode-badge">
            Admin Mode — Edit enabled
          </Badge>
        )}
      </div>

      <div className="max-w-6xl mx-auto p-4 space-y-4">
        <div className="flex gap-2">
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && fetchHistory()}
            placeholder="Search by bundle code..."
            data-testid="search-input"
          />
          <Button onClick={fetchHistory} data-testid="search-btn">
            <Search className="w-4 h-4" />
          </Button>
        </div>

        <Card>
          <CardContent className="pt-4">
            {loading ? (
              <p className="text-center text-gray-400 py-8">Loading...</p>
            ) : receipts.length === 0 ? (
              <p className="text-center text-gray-400 py-8">No receipts found</p>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Receipt ID</TableHead>
                      <TableHead>Bundle</TableHead>
                      <TableHead>Design</TableHead>
                      <TableHead>Shade</TableHead>
                      <TableHead className="text-center">Sent</TableHead>
                      <TableHead className="text-center">Received</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>By</TableHead>
                      <TableHead>Date</TableHead>
                      {isAdmin && <TableHead className="text-center">Actions</TableHead>}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {receipts.map((r) => (
                      <TableRow key={r.receipt_id} data-testid={`history-row-${r.bundle_code}`}>
                        <TableCell className="font-mono text-xs">{r.receipt_id}</TableCell>
                        <TableCell className="font-semibold">{r.bundle_code}</TableCell>
                        <TableCell>{r.design_name || '-'}</TableCell>
                        <TableCell>{r.shade || '-'}</TableCell>
                        <TableCell className="text-center">{r.sent_qty}</TableCell>
                        <TableCell className="text-center font-bold">
                          {r.received_qty}
                          {r.qty_edited_at && (
                            <span
                              className="ml-1 text-[10px] text-amber-700 italic"
                              title={`Edited ${new Date(r.qty_edited_at).toLocaleString()} by ${r.qty_edited_by || ''}`}
                            >
                              (edited)
                            </span>
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1">
                            {statusIcon(r.receipt_status)}
                            <Badge className={
                              r.receipt_status === 'confirmed' ? 'bg-green-100 text-green-800' :
                              r.receipt_status === 'shortage' ? 'bg-red-100 text-red-800' :
                              'bg-amber-100 text-amber-800'
                            }>
                              {r.receipt_status}
                              {r.shortage_qty > 0 && ` (-${r.shortage_qty})`}
                              {r.excess_qty > 0 && ` (+${r.excess_qty})`}
                            </Badge>
                          </div>
                        </TableCell>
                        <TableCell className="text-sm">{r.received_by_name || '-'}</TableCell>
                        <TableCell className="text-xs text-gray-500">{r.received_at ? new Date(r.received_at).toLocaleDateString() : '-'}</TableCell>
                        {isAdmin && (
                          <TableCell className="text-center">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => openEdit(r)}
                              className="h-7 px-2 border-amber-400 text-amber-800 hover:bg-amber-50"
                              data-testid={`edit-qty-btn-${r.bundle_code}`}
                            >
                              <Pencil className="w-3.5 h-3.5 mr-1" /> Edit Qty
                            </Button>
                          </TableCell>
                        )}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}

            {total > 20 && (
              <div className="flex justify-between items-center mt-4 pt-3 border-t">
                <span className="text-sm text-gray-500">Page {page + 1} of {Math.ceil(total / 20)}</span>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0}>Prev</Button>
                  <Button variant="outline" size="sm" onClick={() => setPage(p => p + 1)} disabled={(page + 1) * 20 >= total}>Next</Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Admin: Edit Received Qty (per-row) */}
      <Dialog open={!!editRow} onOpenChange={(o) => { if (!o) closeEdit(); }}>
        <DialogContent data-testid="edit-qty-dialog">
          <DialogHeader>
            <DialogTitle>Edit Received Qty (Admin)</DialogTitle>
          </DialogHeader>
          {editRow && (
            <div className="space-y-3 py-2 text-sm">
              <div className="bg-amber-50 border border-amber-200 rounded p-3 text-amber-900 text-xs">
                Increasing qty claims fresh PI USNs. Decreasing releases trailing USNs back to the pool —
                blocked if any of them have moved downstream (folded, pressed, packed, etc.).
              </div>
              <div className="bg-gray-50 border rounded p-3 text-xs space-y-1">
                <p>
                  <span className="text-gray-500">Bundle:</span>{' '}
                  <span className="font-mono font-bold">{editRow.bundle_code}</span>
                </p>
                <p>
                  <span className="text-gray-500">Design:</span> {editRow.design_name || '-'} &nbsp;
                  <span className="text-gray-500">Shade:</span> {editRow.shade || '-'}
                </p>
                <p>
                  <span className="text-gray-500">Sent Qty:</span> {editRow.sent_qty} &nbsp;
                  <span className="text-gray-500">Current Received Qty:</span>{' '}
                  <span className="font-bold">{editRow.received_qty}</span>
                </p>
              </div>
              <div>
                <label className="text-xs font-medium text-gray-700 mb-1 block">New Received Qty *</label>
                <Input
                  type="number" min="0"
                  value={editNewQty}
                  onChange={(e) => setEditNewQty(e.target.value)}
                  placeholder="0"
                  data-testid="edit-qty-new-qty"
                  autoFocus
                />
              </div>
              <div>
                <label className="text-xs font-medium text-gray-700 mb-1 block">Reason</label>
                <Input
                  value={editReason}
                  onChange={(e) => setEditReason(e.target.value)}
                  placeholder="Audit note (optional)"
                  data-testid="edit-qty-reason"
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={closeEdit} data-testid="edit-qty-cancel">Cancel</Button>
            <Button
              onClick={submitEdit}
              disabled={editLoading || editNewQty === ''}
              className="bg-amber-600 hover:bg-amber-700 text-white"
              data-testid="edit-qty-confirm-btn"
            >
              {editLoading ? 'Updating…' : 'Apply Qty Change'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ProcessingInwardHistory;
